import { z } from 'zod';
import { nullStr, nullNum } from 'schemas/nullable';

/** Keeps array items that pass schema validation; drops invalid ones instead of failing the whole array. */
function partialArraySchema<T extends z.ZodType>(schema: T) {
  return z.preprocess(
    val => (Array.isArray(val) ? val : []),
    z.array(z.unknown()).transform(items => {
      const valid: z.infer<T>[] = [];
      for (const item of items) {
        const parsed = schema.safeParse(item);
        if (parsed.success) valid.push(parsed.data);
      }
      return valid;
    }),
  );
}

const zoneRefSchema = z
  .object({
    _id: z.string().default(''),
    type: z.string().default(''),
    code: z.string().default(''),
    color: z.string().default(''),
  })
  .nullable()
  .default(null);

const locationRefSchema = z
  .object({
    _id: z.string().default(''),
    code: z.string().default(''),
    type: z.string().default(''),
    zoneId: zoneRefSchema,
  })
  .nullable()
  .default(null);

const waveLineProductSchema = z
  .object({
    _id: z.string().default(''),
    p_sku: z.string().default(''),
    p_title: z.string().default(''),
    p_description: nullStr(),
    upc: nullStr(),
    p_images: z
      .object({
        display_image: z.string(),
      })
      .nullable()
      .optional(),
  })
  .nullable()
  .default(null);

const waveLineInventoryQuantitySchema = z
  .object({
    allocated: nullNum(),
    damaged: nullNum(),
    transfer: nullNum(),
    shipment: nullNum(),
    available: nullNum(),
    total: nullNum(),
    uom: nullStr(),
  })
  .loose();

const waveLineInventoryInnerSchema = z
  .object({
    _id: nullStr(),
    customerOrganizationId: nullStr(),
    inventoryType: nullStr(),
    lotNumber: nullStr(),
    cartonAttributes: z.object({ alias: nullStr() }).optional(),
    quantity: waveLineInventoryQuantitySchema.optional(),
  })
  .loose();

export const replenishmentWaveLineSchema = z
  .object({
    _id: z.string(),
    sourcePalletId: nullStr(),
    destinationLocationId: locationRefSchema,
    status: z.string().default('draft'),
    priority: nullStr(),
    replenishmentWaveLineSequence: nullNum(),
    replenishmentQtyPicked: nullNum(),
    replenishmentQtyPut: nullNum(),
    productId: waveLineProductSchema,
    sourceLocationId: locationRefSchema,
    inventoryId: waveLineInventoryInnerSchema.nullable().optional(),
    suggestedQuantity: z.number().default(0),
    suggestedQuantityCartons: nullNum(),
    referenceType: nullStr(),
    expiresAt: nullStr(),
    __v: nullNum(),
    createdAt: nullStr(),
    updatedAt: nullStr(),
    replenishmentLineStatus: z.string().default(''),
  })
  .loose();

export const replenishmentWaveSchema = z
  .object({
    _id: z.string(),
    waveKind: z.string().default(''),
    status: z.string().default('draft'),
    transferPalletId: nullStr(),
    organizationId: nullStr(),
    warehouseId: nullStr(),
    waveNumber: z.string().default(''),
    initiatedBy: nullStr(),
    createdAt: nullStr(),
    updatedAt: nullStr(),
    __v: nullNum(),
    lines: z.array(replenishmentWaveLineSchema).default([]),
  })
  .loose();

export const replenishmentWavesListResponseSchema = z.object({
  data: partialArraySchema(replenishmentWaveSchema),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

export const replenishmentWavesListFilterSchema = z.object({
  limit: z.number(),
  page: z.number(),
});

export type ReplenishmentWaveLine = z.infer<typeof replenishmentWaveLineSchema>;
export type ReplenishmentWave = z.infer<typeof replenishmentWaveSchema>;
export type ReplenishmentWavesListResponse = z.infer<
  typeof replenishmentWavesListResponseSchema
>;
export type ReplenishmentWavesListFilter = z.infer<
  typeof replenishmentWavesListFilterSchema
>;
